/**
 * meetingSession.component.tsx
 * Secured Communication
 *
 * Created by: Dylan Knight
 * Copyright (c) 2021 Secured Communications. All rights reserved.
 *
**/

import bluebird from "bluebird";
import React from "react";

import Check from "@mui/icons-material/Check";
import LogoutIcon from "@mui/icons-material/Logout";
//import MenuIcon from "@mui/icons-material/Menu";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ScreenShareIcon from "@mui/icons-material/ScreenShare";
import StopScreenShareIcon from "@mui/icons-material/StopScreenShare";
import VideocamIcon from "@mui/icons-material/Videocam";
import VideocamOffIcon from "@mui/icons-material/VideocamOff";
import { ClickAwayListener, Divider, Fab, IconButton, ListItemIcon, ListItemText, ListSubheader, MenuItem, 
  MenuList, Paper, Popper, ToggleButton, ToggleButtonGroup, Tooltip } from "@mui/material";


import { MeetingContext, HostControlsProvider } from "./meeting.context";

import { SessionStreamsComponent } from "./sessionStreams.component";
import { ErrorSnackBar } from "../components/errorSnackBar.component";

import { GUEST_STRING, Meeting, MeetingType } from "../models/meeting";
import { SubscriberStream, AspectRatio } from "../models/stream";
import { authServiceFactory } from "../services/auth.service";
import { conferenceServiceFactory } from "../services/conferencing.service";
//import { groupServiceFactory } from "../services/group.service";
import { workerServiceFactory } from "../services/worker.service";
import { ChannelMeetingMessage } from "../models/broadcast";
import { MemberType } from "../models/auth";


interface Props {
  meeting: Meeting,
  session: OT.Session,
  token: string,
  isHost: boolean,
  sendVideo: boolean,
  sendAudio: boolean,
  videoSrcID?: string,
  audioSrcID?: string,
  onToggleVideo?: () => void,
  onToggleAudio?: () => void,
  disabledAudio?: () => void,
  setVideoSourceID?: (srcID: string) => void,
  setAudioSourceID?: (srcID: string) => void,
  onToggleFullscreen?: () => void,   // unused in meetings for now
  onLeaveRoom: () => void
}

interface State {
  errMsg: string,
  connecting: boolean,
  screenPublisher: OT.Publisher | null,
  aspectRatio: AspectRatio,
  openMenu: boolean,
  anchorEl: HTMLElement | null,
  canScreenShare: boolean,
  isForceMuteAllActive: boolean,
  videoDevices: Array<OT.Device>,
  audioDevices: Array<OT.Device>
}

export class MeetingSessionComponent extends React.Component<Props, State> implements HostControlsProvider {

  static contextType: React.Context<MeetingContext> = MeetingContext;
  declare context: React.ContextType<typeof MeetingContext>;

  private container: React.RefObject<HTMLDivElement | null>;
  private leaving: boolean;


  private streams: Array<SubscriberStream>;

  /**
   * Since state might not update immediately, we need to keep track of the 
   * publisher as soon as it's created in order to destroy it
   */
  private publisher: OT.Publisher | null;

  get isForceMuteAllActive(): boolean {
    return this.state.isForceMuteAllActive;
  }

  private newWaitingAudio: React.Ref<HTMLAudioElement>;

  constructor(props: Props) {
    super(props);
    this.leaving = false;
    this.container = React.createRef<HTMLDivElement>();
    this.newWaitingAudio = React.createRef<HTMLAudioElement>();

    this.newMemberCB = this.newMemberCB.bind(this);
    this.remWaitingMemberCB = this.remWaitingMemberCB.bind(this);
    this.onStreamCreated = this.onStreamCreated.bind(this);
    this.onStreamDestroyed = this.onStreamDestroyed.bind(this);
    this.onSessionReconnecting = this.onSessionReconnecting.bind(this);
    this.onSessionReconnected = this.onSessionReconnected.bind(this);
    this.onSessionDisconnected = this.onSessionDisconnected.bind(this);
    this.createPublisher = this.createPublisher.bind(this);
    this.publisherDestroyed = this.publisherDestroyed.bind(this);
    this.audioForceMuted = this.audioForceMuted.bind(this);
    this.leave = this.leave.bind(this);
    this.clearListeners = this.clearListeners.bind(this);
    this.doOpenMenu = this.doOpenMenu.bind(this);
    this.changeAspectRatio = this.changeAspectRatio.bind(this);
    this.toggleScreenShare = this.toggleScreenShare.bind(this);
    this.setupScreenSharing = this.setupScreenSharing.bind(this);
    this.screenPublisherDestroyed = this.screenPublisherDestroyed.bind(this);

    const fakeStreams: Array<SubscriberStream> = [];
    //for (let i = 0; i < 4; i++) { fakeStreams.push(new SubscriberStream(i)); }
    this.publisher = null;

    this.streams = fakeStreams;

    this.state = {
      errMsg: "",
      connecting: false,
      screenPublisher: null,
      aspectRatio: AspectRatio.widescreen,
      openMenu: false,
      anchorEl: null,
      canScreenShare: false,
      isForceMuteAllActive: false,
      videoDevices: [],
      audioDevices: []
    };
  }

  async componentDidMount() {
    if (this.context) {
      this.context.setStreams(this.streams);
      this.context.setHostControlsProvider(this);
    }
    this.props.session.on("sessionReconnecting", this.onSessionReconnecting);
    this.props.session.on("sessionReconnected", this.onSessionReconnected);
    this.props.session.on("sessionDisconnected", this.onSessionDisconnected);
    this.props.session.on("streamCreated", this.onStreamCreated);
    this.props.session.on("streamDestroyed", this.onStreamDestroyed);
    const workerService = workerServiceFactory();
    workerService.on("newWaiting", this.newMemberCB);
    workerService.on("remWaiting", this.remWaitingMemberCB);

    OT.checkScreenSharingCapability(response => {
      this.setState({ canScreenShare: response.supported });
    });

    try {
      this.setState({ connecting: true });
      let publisher: OT.Publisher | null = null;

      if (this.props.isHost || this.props.meeting.meetingtype !== MeetingType.webinar) {
        publisher = await this.createPublisher();
        this.publisher = publisher;
        const [videoDevices, audioDevices] = await conferenceServiceFactory().getMediaDevices();
        this.setState({
          audioDevices: audioDevices,
          videoDevices: videoDevices
        });
      }
      await new bluebird<void>((resolve, reject) => {
        this.props.session.connect(this.props.token, error => {
          if (error) { return reject(error); }
          resolve();
        });
      });

      await bluebird.all([
        this.refreshWaitingDevices(),
        this.connectPublisher(publisher)
      ]);

    } catch (err) {
      console.error("error connecting:", err);
      const anyerr = err as any;
      if (anyerr.originalName && anyerr.originalName === "NotAllowedError") {
        this.setState({
          errMsg: `Unable to publish your A/V streams.
          Please, ensure you've allowed the site access to your camera and microphone!`});
      } else {
        this.setState({ errMsg: (err as Error).message });
      }
    } finally {
      this.setState({ connecting: false });
    }
  }

  componentWillUnmount() {
    this.clearListeners();
    this.leaving = true;

    this.context.setHostControlsProvider(null);

    if (this.publisher) {
      this.publisher.publishAudio(false);
      this.publisher.publishVideo(false);
      this.publisher.destroy();
    }
  }

  componentDidUpdate(prevProps: Props) {
    if (this.context && this.streams.length !== this.context.streams.length) {
      (this.context as MeetingContext).setStreams(this.streams);
    }
    if (this.context && this.context.hostControlsProvider !== this) {
      this.context.setHostControlsProvider(this);
    }
    if (this.props.sendAudio !== prevProps.sendAudio) {
      this.publisher?.publishAudio(this.props.sendAudio);
    }
    if (this.props.sendVideo !== prevProps.sendVideo) {
      this.publisher?.publishVideo(this.props.sendVideo);
    }
    if (this.props.videoSrcID && this.props.videoSrcID !== prevProps.videoSrcID) {
      this.publisher?.setVideoSource(this.props.videoSrcID);
    }
    if (this.props.audioSrcID && this.props.audioSrcID !== prevProps.audioSrcID) {
      this.publisher?.setAudioSource(this.props.audioSrcID);
    }
  }

  async newMemberCB(mtgMsg: ChannelMeetingMessage) {
    const meetingContext = this.context as MeetingContext;
    const arr = meetingContext.waitingDevices ? Array.from(meetingContext.waitingDevices) : [];
    const wdID = mtgMsg.waitingDeviceID || mtgMsg.fromDeviceID || mtgMsg.fromUserID;
    if (meetingContext.meeting && meetingContext.meeting._id !== mtgMsg.meetingID) {
      console.log("device is for different meeting");
      return;
    }
    if (arr.find(w => w._id === wdID)) {
      console.log("already have waiting device in list:", mtgMsg);
      return;
    }
    arr.push({
      _id: wdID,
      accountID: mtgMsg.accountID,
      meetingID: mtgMsg.meetingID,
      userID: mtgMsg.fromUserID,
      deviceID: mtgMsg.fromDeviceID || "",
      phoneNumber: mtgMsg.fromPhoneNumber,
      allowed: mtgMsg.allowed || false,
      firstname: mtgMsg.firstname || "unknown",
      lastname: mtgMsg.lastname || ""
    });
    meetingContext.setWaitingDevices(arr);
    (this.newWaitingAudio as React.RefObject<HTMLAudioElement>).current?.play();
  }

  async remWaitingMemberCB(mtgMsg: ChannelMeetingMessage) {
    const meetingContext = this.context as MeetingContext;
    const arr = meetingContext.waitingDevices ? Array.from(meetingContext.waitingDevices) : [];
    let index = arr.findIndex(d => d.userID === mtgMsg.fromUserID);
    let updated = false;
    while (index >= 0) {
      console.log("rem index:", index);
      updated = true;
      arr.splice(index, 1);
      index = arr.findIndex(d => d.userID === mtgMsg.fromUserID);
    }
    if (updated) {
      meetingContext.setWaitingDevices(arr);
    }
  }

  async refreshWaitingDevices() {
    if (!this.props.isHost) { return; }
    const membership = authServiceFactory().currentAccountMembership;
    if (!membership) { return; }
    const devs = await conferenceServiceFactory().getWaitingDevices(
      membership.accountID, this.props.meeting._id);
    this.context.setWaitingDevices(devs);
  }


  bootStream(stream: SubscriberStream): void {
    if (!this.props.isHost || !stream.stream) { return; }
    this.props.session.forceDisconnect(stream.stream.connection, err => {
      if (err) {
        console.error("Error forcing disconnection:", err);
      }
    });
  }

  async forceMuteAudio(stream: SubscriberStream): Promise<void> {
    if (!this.props.isHost || !stream.stream) { return; }
    try {
      await this.props.session.forceMuteStream(stream.stream);
      this.setState({ isForceMuteAllActive: true });
    } catch (err) {
      console.error("Error force muting:", err);
    }
  }

  forceUnpublish(stream: SubscriberStream): void {
    if (!this.props.isHost || !stream.stream) { return; }
    this.props.session.forceUnpublish(stream.stream, err => {
      if (err) {
        console.error("Error forcing unpublish:", err);
      }
    });
  }

  async forceMuteAllButMe(): Promise<void> {
    if (!this.props.isHost) { return; }
    try {
      const excluded: Array<OT.Stream> = [];
      if (this.publisher && this.publisher.stream) {
        excluded.push(this.publisher.stream);
      }
      if (this.state.screenPublisher && this.state.screenPublisher.stream) {
        excluded.push(this.state.screenPublisher.stream);
      }
      await this.props.session.forceMuteAll(excluded);
      this.setState({ isForceMuteAllActive: true });
    } catch (err) {
      console.error("Error force muting all:", err);
    }
  }

  async onStreamCreated(event: OT.Event<"streamCreated", OT.Session> & { stream: OT.Stream }) {
    const index = this.streams.findIndex(sub => sub.streamId === event.stream.streamId);
    if (index < 0) {
      const arr = Array.from(this.streams);
      const stream = new SubscriberStream(event.stream);
      arr.push(stream);
      if (stream.stream && this.context.muteNewStreams) {
        await this.props.session.forceMuteStream(stream.stream);
      }
      this.streams = arr;
      this.context.setStreams(arr);
    }
  }

  onStreamDestroyed(event: OT.Event<"streamDestroyed", OT.Session> & { stream: OT.Stream, reason: string }) {
    const index = this.streams.findIndex(sub => {
      return sub.streamId === event.stream.streamId;
    });
    console.log("onStreamDestroyed:", this.streams);
    if (index >= 0) {
      const arr = Array.from(this.streams);
      arr.splice(index, 1);
      //this.setState({streams: arr});
      this.streams = arr;
      this.context.setStreams(arr);
    }
  }

  async createPublisher(): Promise<OT.Publisher> {
    // NOTE: this relies on the div with id="publisher" existing in SessionStreamsComponent
    const membership = authServiceFactory().currentAccountMembership;
    /*const pubName = membership ? JSON.stringify({
      name: membership.firstname + " " + membership.lastname,
      userID: membership.userID,
      deviceID: groupServiceFactory().deviceID
    }) : "";*/
    // if the user is a temp user, add (Guest) so others are aware
    const pubName = membership ? `${membership.firstname} ${membership.lastname} ${membership.membertype === MemberType.temp ?  GUEST_STRING: ""}` : "";
    const publisher = await conferenceServiceFactory().createPublisher("publisher", pubName,
      this.props.sendAudio, this.props.sendVideo);
    if (this.props.videoSrcID) {
      publisher.setVideoSource(this.props.videoSrcID);
    }
    if (this.props.audioSrcID) {
      publisher.setAudioSource(this.props.audioSrcID);
    }
    publisher.on("destroyed", this.publisherDestroyed);
    publisher.on("muteForced", this.audioForceMuted);
    publisher.on("mediaStopped", event => {
      console.log("media stopped:", event);
    });
    return publisher;
  }

  connectPublisher(publisher: OT.Publisher | null): Promise<void> {
    if (!publisher) { return bluebird.resolve(); }
    return new bluebird<void>((resolve, reject) => {
      this.props.session.publish(publisher, error => {
        if (error) {
          publisher.destroy();
          return reject(error);
        }
        resolve();
      });
    });
  }

  async publisherDestroyed(event: any) {
    console.log("publisher destroyed:", event);
    this.publisher?.off("streamDestroyed", this.publisherDestroyed);
    this.publisher?.off("muteForced", this.audioForceMuted);
    if (!this.leaving) {
      this.publisher = null;
      const publisher = await this.createPublisher();
      await this.connectPublisher(publisher);
      this.publisher = publisher;
    }
  }

  audioForceMuted(event: any) {
    console.log("mute forced:", event);
    this.props.disabledAudio && this.props.disabledAudio();
  }

  onSessionReconnecting(event: OT.Event<"sessionReconnecting", OT.Session>) {
    console.log("Session attempting reconnect:", event);
  }

  onSessionReconnected(event: OT.Event<"sessionReconnected", OT.Session>) {
    console.log("Session reconnected:", event);
  }

  onSessionDisconnected(event: OT.Event<"sessionDisconnected", OT.Session>) {
    console.log("Session disconnected:", event);
    this.leaving = true;
    const reason = (event as any).reason;
    if (reason === "networkDisconnected") {
      this.setState({
        errMsg:
          "You lost your internet connection. Please check your connection and try connecting again."
      });
    } else if (reason == "forceDisconnected") {
      this.setState({
        errMsg: "You were removed from this meeting."
      });
      this.context.setStreams([]);
    }
  }

  leave() {
    this.leaving = true;
    if (this.publisher) {
      this.publisher.publishVideo(false);
      this.publisher.publishAudio(false);
    }
    this.clearListeners();
    this.props.onLeaveRoom();
  }

  clearListeners() {
    if (this.props.session) {
      this.props.session.off("sessionReconnecting", this.onSessionReconnecting);
      this.props.session.off("sessionReconnected", this.onSessionReconnected);
      this.props.session.off("sessionDisconnected", this.onSessionDisconnected);
      this.props.session.off("streamCreated", this.onStreamCreated);
      this.props.session.off("streamDestroyed", this.onStreamDestroyed);
    }
    const workerService = workerServiceFactory();
    workerService.off("newWaiting", this.newMemberCB);
    workerService.off("remWaiting", this.remWaitingMemberCB);
  }

  doOpenMenu(event: React.MouseEvent<HTMLButtonElement>) {
    this.setState({ anchorEl: event.currentTarget, openMenu: !this.state.openMenu });
  }

  changeAspectRatio(_event: React.MouseEvent<HTMLElement>, ratio: AspectRatio) {
    this.setState({ aspectRatio: ratio });
  }

  toggleScreenShare() {
    if (this.state.screenPublisher) {
      this.state.screenPublisher.off("streamDestroyed", this.screenPublisherDestroyed);
      this.state.screenPublisher.destroy();
      this.setState({ screenPublisher: null });
    } else {
      this.setupScreenSharing();
    }
  }

  async setupScreenSharing() {
    try {
      const membership = authServiceFactory().currentAccountMembership;
      const pubName = membership ?
        membership.firstname + "'s Screen" : "Screen Share";

      const publisher = await conferenceServiceFactory().createScreenShare(
        "screen-share", pubName, this.props.sendAudio);

      await new bluebird<OT.Publisher>((resolve, reject) => {
        this.props.session.publish(publisher, error => {
          if (error) { return reject(error); }
          resolve();
        });
      });

      publisher.on("streamDestroyed", this.screenPublisherDestroyed);
      this.setState({ screenPublisher: publisher });

    } catch (err) {
      const anyErr = err as any;
      if (anyErr.originalName && anyErr.originalName === "NotAllowedError") {
        this.setState({
          errMsg: "Please, enable access to screenshare.",
          screenPublisher: null
        });
      }
      else if (!anyErr.originalName || anyErr.originalName != "NotAllowedError") {
        console.error("Error trying to share screen:", err);
        this.setState({
          errMsg: (err as Error).message,
          screenPublisher: null
        });
      }
    }
  }
  screenPublisherDestroyed() {
    this.setState({ screenPublisher: null });
  }

  render(): React.ReactElement<any, any> {
    const meetingContext = this.context as MeetingContext;
    return <div className="room-session-container" ref={this.container}>
      <ErrorSnackBar errMsg={this.state.errMsg} didClose={() => this.setState({ errMsg: "" })} />

      <SessionStreamsComponent
        session={this.props.session}
        connecting={this.state.connecting}
        screenPublisher={this.state.screenPublisher}
        isPublishing={this.publisher !== null}
        streams={this.context.streams}
        availableWidth={this.container.current?.clientWidth}
        availableHeight={this.container.current?.clientHeight}
        aspectRatio={this.state.aspectRatio} />

      <div className="room-controls-backdrop">
        <div className="room-controls">
          <IconButton size="medium"
            disableRipple disableTouchRipple onClick={this.props.onToggleVideo}
              aria-label={this.props.sendVideo ? "Disable Video" : "Enable Video"}>
            <Tooltip title={this.props.sendVideo ? "Disable Video" : "Enable Video"} arrow>
              {this.props.sendVideo ? <VideocamIcon /> : <VideocamOffIcon />}
            </Tooltip>
          </IconButton>
          <IconButton size="medium"
            disableRipple disableTouchRipple onClick={this.props.onToggleAudio}
              aria-label={this.props.sendAudio ? "Disable Audio" : "Enable Audio"}>
            <Tooltip title={this.props.sendAudio ? "Disable Audio" : "Enable Audio"} arrow>
              {this.props.sendAudio ? <MicIcon /> : <MicOffIcon />}
            </Tooltip>
          </IconButton>
          {this.state.canScreenShare && <IconButton size="medium" onClick={this.toggleScreenShare}
              aria-label={this.state.screenPublisher ? "Stop Sharing" : "Share Screen"}>
            <Tooltip title={this.state.screenPublisher ? "Stop Sharing" : "Share Screen"} arrow>
              {this.state.screenPublisher ? <StopScreenShareIcon /> : <ScreenShareIcon />}
            </Tooltip>
          </IconButton>}
          <IconButton disableRipple disableTouchRipple
            aria-label="huddle-more-menu" aria-haspopup="true"
            size="small" onClick={this.doOpenMenu}>
            <MoreVertIcon fontSize="small" />
          </IconButton>
          {this.props.isHost && <IconButton disableRipple disableTouchRipple aria-label="toggle members"
            size="small"
            className="members-view-toggle-btn"
            onClick={meetingContext.toggleMembersView}>
            <i className="fa-solid fa-users has-badge" data-member-count={meetingContext.waitingDevices?.length || undefined} />
          </IconButton>}
          <Fab className="leave-room-btn" size="small" color="secondary" aria-label="Leave Meeting"
            disableRipple disableTouchRipple onClick={this.leave}>
            <LogoutIcon />
          </Fab>
          <Popper open={this.state.openMenu} anchorEl={this.state.anchorEl} disablePortal placement="top">
            <Paper>
              <ClickAwayListener onClickAway={() => this.setState({ openMenu: false })}>
                <MenuList dense>
                  {this.state.videoDevices.length > 0 && <div>
                    <ListSubheader>Video Inputs</ListSubheader>
                    {this.state.videoDevices.map((d, i) =>
                      <MenuItem key={d.deviceId}
                        onClick={() => this.props.setVideoSourceID && this.props.setVideoSourceID(d.deviceId)}>
                        <ListItemIcon>
                          {i === 0 && !this.props.videoSrcID || this.props.videoSrcID === d.deviceId ? <Check /> : <></>}
                        </ListItemIcon>
                        <ListItemText>{d.label}</ListItemText>
                      </MenuItem>)}
                    <Divider />
                  </div>}
                  {this.state.audioDevices.length > 0 && <div>
                    <ListSubheader>Audio Inputs</ListSubheader>
                    {this.state.audioDevices.map((d, i) =>
                      <MenuItem key={d.deviceId}
                        onClick={() => this.props.setAudioSourceID && this.props.setAudioSourceID(d.deviceId)}>
                        <ListItemIcon>
                          {i === 0 && !this.props.audioSrcID || this.props.audioSrcID === d.deviceId ? <Check /> : <></>}
                        </ListItemIcon>
                        <ListItemText>{d.label}</ListItemText>
                      </MenuItem>)}
                    <Divider />
                  </div>}
                  <ListSubheader>Aspect Ratio</ListSubheader>
                  <MenuItem className="aspect-chooser-container" disableRipple disableTouchRipple>
                    <ToggleButtonGroup exclusive size="small" value={this.state.aspectRatio}
                      onChange={this.changeAspectRatio}>
                      <ToggleButton value={AspectRatio.square} disableRipple disableTouchRipple>1/1</ToggleButton>
                      <ToggleButton value={AspectRatio.tv} disableRipple disableTouchRipple>4/3</ToggleButton>
                      <ToggleButton value={AspectRatio.widescreen} disableRipple disableTouchRipple>16/9</ToggleButton>
                      <ToggleButton value={AspectRatio.anamorphic} disableRipple disableTouchRipple>2.39/1</ToggleButton>
                      <ToggleButton value={AspectRatio.phone} disableRipple disableTouchRipple>1/2</ToggleButton>
                    </ToggleButtonGroup>
                  </MenuItem>
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Popper>
        </div>
      </div>
      <audio ref={this.newWaitingAudio} src="/aud/notification-tone-swift-gesture.ogg" />
    </div>;
  }
}
